import { IFloorplanTableCalculated } from 'floorplan-v2/app/services/PositionService/types';
import { BookingType, IBooking } from 'shared-types/SharedTypes';
import { Viewport } from 'pixi-viewport';
import { IBookingsByTable, IBookingsByTableItem, } from 'shared-types';
import DateUtilsService from 'external-services/date-utils-service';
import {BookingAvailabilityService} from "external-services/booking-availability-service/index"
import { IBookingSubset } from "external-services/booking-availability-service/types"
import { floorplanPixiContainerId } from 'floorplan-v2/app/appRoot/types'
import { sortBy } from 'lodash-es'
import {Rectangle} from "pixi.js";
import { DraggedBookingSourceEnum, IDragTableBookingSlot } from 'floorplan-v2/app/reducers/tableDrag/types';

const leftBufferBCLDrag = 50; // distance from edge to start scrolling - left buffer for BCLDrag
const rightBufferPlusBtn = 100; // space for the plus bton on the right
const headerBuffer = 125;
const topEdgeBuffer = headerBuffer + 175; // Top header buffer. Allows for banner and section filters
const speed = 25; // speed of scrolling
const NS = 'FloorplanBookingDragService';

let floorplanPixiContainer: HTMLElement;
let boundingTop = 0
let boundingLeft = 0

class FloorplanBookingDragService {
  /**
   * returns true if booking drags over another table that doesn't contain any of its own tables (eg in a table join)
   */
  shouldHighlightTableOnDrag(
    targetTable: IFloorplanTableCalculated,
    dragFromTableId: string,
    draggedBooking: IBooking
  ): boolean {
    if (!draggedBooking) {
      return false;
    }

    const isJoinedToDraggedFromTable = draggedBooking.tables.find((t) => t._id === targetTable._id);

    // ignore when "dragged on table" is joined to self
    if (isJoinedToDraggedFromTable) {
      return false;
    }

    return true;
  }

  /**
   * returns true if target table has bookings that have a table join AND the draggedBooking also has a table join
   */
  doBothBookingsHaveTableJoins(targetTableBookings: IBooking[], draggedBooking: IBooking): boolean {
    if (!targetTableBookings?.length || !draggedBooking) {
      return false;
    }

    const draggedBookingHasTableJoin = draggedBooking.tables.length > 1;

    const result = !!targetTableBookings.filter((b) => {
      const targetBookingHasTableJoin = b.tables.length > 1;
      return draggedBookingHasTableJoin && targetBookingHasTableJoin;
    }).length;

    return result;
  }



  dragPanHandler(
    viewport: Viewport,
    draggedBookingPoint: { x: number; y: number },
    draggedBooking: IBooking,
    width: number,
    disableLeftScroll: boolean,
  ) {
    if (draggedBookingPoint && draggedBooking && viewport) {
      const bookingCardListWidth = width;
      let changed = false;
      if (
        draggedBookingPoint.x < bookingCardListWidth + leftBufferBCLDrag &&
        viewport.x < bookingCardListWidth + rightBufferPlusBtn && !disableLeftScroll
      ) {
        // mouse is on the left edge - do not scroll
        if (viewport.x < 0) {
          viewport.x += speed;
          changed = true;
        }

      } else if (
        draggedBookingPoint.x > window.innerWidth - leftBufferBCLDrag &&
        viewport.x > -viewport.worldWidth + bookingCardListWidth + rightBufferPlusBtn // 100 is the right buffer for plus btn
      ) {
        // mouse is on the right edge, scroll right
        if ((draggedBookingPoint.x - viewport.x - bookingCardListWidth - rightBufferPlusBtn) < viewport.width) {
          viewport.x -= speed;
          changed = true;
        }
      }

      if (draggedBookingPoint.y < leftBufferBCLDrag + headerBuffer && viewport.y < topEdgeBuffer) {
        // mouse is on the top edge, do not scroll
        if (viewport.y < 0) {
          viewport.y += speed;
          changed = true;
        }

      } else if (
        draggedBookingPoint.y > window.innerHeight - leftBufferBCLDrag &&
        viewport.y > -viewport.worldHeight + headerBuffer
      ) {
        // mouse is on the bottom edge, scroll down
        if ((draggedBookingPoint.y - viewport.y - headerBuffer - rightBufferPlusBtn) < viewport.height) {
          viewport.y -= speed;
          changed = true;
        }

      }

      if (changed) {
        /**
         * Needs to update hitarea while dragging, so that the dragged booking hover effects can take place.
         * viewport.forceHitArea should be set back to null when dragging has finished, so that it goes back
         * to automatic settings
         */
        const newHitArea = new Rectangle(viewport.left, viewport.top, viewport.worldScreenWidth, viewport.worldScreenHeight);
        viewport.forceHitArea = newHitArea;
      }
    } else {
      viewport.forceHitArea = null;
    }
  }

  getBookingsByTable(
    bookingsByTable: IBookingsByTable,
    tableId: string,
    bookingIds?: string[]
  ): IBookingsByTableItem {
    const item = bookingsByTable[tableId];
    if (!item) {
      return null;
    }
    if (!bookingIds?.length) {
      return item;
    }
    return {
      table: item.table,
      bookings: item.bookings.filter((b) => bookingIds.some((bId) => b._id === bId)),
    };
  }

  checkIfWaitlistBookingOverlap({
    floorplanBookings,
    dragToTableId,
    selectedFloorplanTime,
    dataDragToTable,
    booking
  }: {
    floorplanBookings: IBooking[],
    dragToTableId: string,
    selectedFloorplanTime: Date,
    dataDragToTable: IBookingsByTableItem,
    booking: IBooking
  }): boolean {
    const nextBookingsOnTable = sortBy(floorplanBookings.filter(b => {
      const tableIds = b.tables.map(t => t._id);
      return tableIds.includes(dragToTableId) && b.time > selectedFloorplanTime
    }), ['time']);

    let sharedTableCapacity = 0
    const isShared = dataDragToTable?.table?.shared
    if (isShared) {
      // get capacity for shared table
      const capacityPockets = booking ? BookingAvailabilityService.findBookingSlotsForSharedTables({
        requestedTime: selectedFloorplanTime,
        tableBookings: dataDragToTable.bookings as unknown as IBookingSubset[] || [],
        requestedDuration: Number(booking?.duration),
        bookingSlots: []
      }) : [0]
      sharedTableCapacity = (capacityPockets?.length > 0 ? Math.max(...capacityPockets) : 0);
    }
    const isOverlap = nextBookingsOnTable.length
      ? DateUtilsService.isAfterDate(
          DateUtilsService.addMinutes(selectedFloorplanTime, Number(booking.duration)),
          nextBookingsOnTable[0].time
        ) && (isShared ? dataDragToTable?.table?.capacity < sharedTableCapacity + booking?.people : true)
      : false;
    return isOverlap
  }

  checkForWaitlistDrag(draggedBooking: IBooking) {
    return Boolean(draggedBooking) && draggedBooking.bookingType === BookingType.WaitList;
  }

  checkForBookingCardDragged(draggedBooking: IBooking, draggedBookingSource: DraggedBookingSourceEnum) {
    return Boolean(draggedBooking) && draggedBookingSource === DraggedBookingSourceEnum.BookingCardList && draggedBooking.bookingType === BookingType.Booking
  }

  checkForDisabledSlotWhileWaitlistDrag(isTimeNow: boolean, slotIndex: number, firstSlotBookingsLength: number): boolean {
    if (!isTimeNow) {
      return true;
    }
    // if the 1st slot has bookings disable all the slots
    if (firstSlotBookingsLength > 0) {
      return true;
    }
    // if the 1st slot is empty then all the other slots will be disabled
    else if (slotIndex > 0) {
      return true;
    }
    return false;
  }

  checkForDisabledSlotWhileDrag(draggedBookingEndTime: Date, slotStartTime: Date, slotEndTime: Date) {
    return draggedBookingEndTime.getTime() < slotStartTime.getTime() || draggedBookingEndTime.getTime() > slotEndTime.getTime();
  }

  checkForSlotOnSameTable(draggedTableId: string, toTableId: string): boolean {
    return draggedTableId === toTableId;
  }

  getFloorplanPixiContainerBounding() {
    if (!floorplanPixiContainer || !boundingTop) {
      floorplanPixiContainer = document.getElementById(floorplanPixiContainerId);
    }
    const floorplanBounding = floorplanPixiContainer.getBoundingClientRect();
    if (floorplanBounding.top > 0) {
      boundingTop = floorplanBounding.top
      boundingLeft = floorplanBounding.left
    }
    return {
      boundingTop,
      boundingLeft
    }
  }

  getDraggedTableId(dragFromTableBookingSlot: IDragTableBookingSlot, draggedBooking: IBooking, tableId: string) {
    if(draggedBooking && !dragFromTableBookingSlot) {
      const isDragBookingOnSameTable = draggedBooking.tables.find((t) => t._id === tableId);
      return isDragBookingOnSameTable ? tableId : draggedBooking.tables[0]?._id
    }
    return dragFromTableBookingSlot?.tableId
  }

}

const instance = new FloorplanBookingDragService();
export default instance as FloorplanBookingDragService;
